import React, { useState, useEffect } from 'react'
import { Modal, Button, Spinner, Alert } from 'react-bootstrap'

interface LockUnlockModalProps {
    show: boolean
    onHide: () => void
    onSubmit: (shouldUnlock: boolean) => Promise<void>
    isLocked: boolean
}

const LockUnlockModal: React.FC<LockUnlockModalProps> = ({ show, onHide, onSubmit, isLocked }) => {
    const [loading, setLoading] = useState<boolean>(false)
    const [error, setError] = useState<string | null>(null)

    const handleSubmit = async () => {
        setLoading(true)
        setError(null)

        try {
            await onSubmit(isLocked)
            setLoading(false)
            handleClose() 
        } catch (err) {
            setLoading(false)
            setError('Failed to change account status. Please try again.')
        }
    }

    const handleClose = () => {
        setError(null)
        onHide()
    }

    useEffect(() => {
        if (!show) {
            setError(null)
        }
    }, [show])

    return (
        <Modal show={show} onHide={handleClose}>
            <Modal.Header closeButton>
                <Modal.Title>{isLocked ? 'Unlock Account' : 'Lock Account'}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {error && <Alert variant="danger">{error}</Alert>}
                <p>
                    Are you sure you want to {isLocked ? 'unlock' : 'lock'} this account? 
                    This will {isLocked ? 'allow' : 'prevent'} the user to login.
                </p>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose} disabled={loading}>
                    Cancel
                </Button>
                <Button variant={isLocked ? "success" : "danger"} onClick={handleSubmit} disabled={loading}>
                    {loading ? (
                        <>
                            <Spinner
                                as="span"
                                animation="border"
                                size="sm"
                                role="status"
                                aria-hidden="true"
                            />{' '}
                            {isLocked ? 'Unlocking...' : 'Locking...'}
                        </>
                    ) : (
                        isLocked ? 'Unlock Account' : 'Lock Account'
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    )
}

export default LockUnlockModal
